import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { ModalSkeleton } from "../../../components/Dashboard/SkeletonLoaders";
import { getInsuranceProtectLandlordDetails } from "../../../context/functions/AdminFunction";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import { constants } from "../../../utils/constants";
import FilterDropdown from "./FilterDropdown";
import InsuranceProtectStats from "./InsuranceProtectStats";
import InsuranceProtectTable from "./InsuranceProtectTable";

const LIMIT = constants.LISTING_ROWLIMIT;

const PRODUCT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "insurance", label: "Insurance Project" },
  { value: "skor_protect", label: "Skor Protect" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "paid", label: "Paid" },
  { value: "not_paid", label: "Not paid" },
];

const InsuranceProtectDetailsModal = ({ show, onHide, selectedLandlord }) => {
  const [workspaceFilter, setWorkspaceFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ totalInsurances: 0, paid: 0, notPaid: 0, totalBilled: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [workspaceOptions, setWorkspaceOptions] = useState([]);

  const abortRef = useRef(null);

  const fetchDetails = useCallback(
    async ({ workspaceId, product, status, page }) => {
      const landlordId = selectedLandlord?.landlord_id || selectedLandlord?.landlord?._id;
      if (!landlordId) return;

      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      setLoading(true);
      try {
        const res = await getInsuranceProtectLandlordDetails({
          landlordId,
          workspaceId: workspaceId === "all" ? "" : workspaceId,
          product,
          status,
          page,
          limit: LIMIT,
          signal: abortRef.current.signal,
        });

        if (!res || res.status === "canceled") return;

        if (res.success && res.success_res) {
          setList(res.success_res.data || []);
          setTotal(res.success_res.total || 0);
          setStats({
            totalInsurances: res.success_res.stats?.totalInsurances || 0,
            paid: res.success_res.stats?.paid || 0,
            notPaid: res.success_res.stats?.notPaid || 0,
            totalBilled: res.success_res.stats?.totalBilled || 0,
          });
          if (res.success_res.workspaces?.length) setWorkspaceOptions(res.success_res.workspaces);
        } else {
          setList([]);
          setTotal(0);
          setStats({ totalInsurances: 0, paid: 0, notPaid: 0, totalBilled: 0 });
        }
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          toast.error("Failed to load insurance & Skor Protect details");
        }
      } finally {
        setLoading(false);
      }
    },
    [selectedLandlord]
  );

  useEffect(() => {
    if (!show) return;
    fetchDetails({
      workspaceId: workspaceFilter,
      product: productFilter,
      status: statusFilter,
      page: currentPage,
    });
  }, [show, workspaceFilter, productFilter, statusFilter, currentPage, fetchDetails]);

  useEffect(() => {
    if (show) {
      setWorkspaceFilter("all");
      setProductFilter("all");
      setStatusFilter("all");
      setCurrentPage(1);
      setList([]);
      setWorkspaceOptions([]);
      setStats({ totalInsurances: 0, paid: 0, notPaid: 0, totalBilled: 0 });
    }
  }, [show, selectedLandlord?.landlord_id]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const handleHide = () => {
    if (abortRef.current) abortRef.current.abort();
    onHide();
  };

  const workspaceDropdownOptions = [
    { value: "all", label: "All" },
    ...workspaceOptions.map((ws) => ({ value: ws._id, label: ws.name })),
  ];

  return (
    <Modal
      show={show}
      onHide={handleHide}
      size="xl"
      dialogClassName="modal-90w"
      centered
      scrollable
      className="landlord-metrics-modal"
    >
      <Modal.Header className="landlord-details-header">
        <div className="d-flex justify-content-between align-items-start w-100">
          <div>
            <h4 className="landlord-details-name">
              {selectedLandlord?.landlord?.fname_companyName} {selectedLandlord?.landlord?.lname}
            </h4>
            <p className="landlord-details-email">
              {selectedLandlord?.landlord?.email || "No email provided"}
            </p>
          </div>
          <button className="modal-close-btn" onClick={handleHide}>
            <X size={20} />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body className="px-5 pb-5 pt-0">
        <div className="ip-filters-row mt-4 mb-4">
          <FilterDropdown
            label="Workspaces"
            value={workspaceFilter}
            options={workspaceDropdownOptions}
            onChange={(value) => {
              setCurrentPage(1);
              setWorkspaceFilter(value);
            }}
          />
          <FilterDropdown
            label="Product"
            value={productFilter}
            options={PRODUCT_OPTIONS}
            onChange={(value) => {
              setCurrentPage(1);
              setProductFilter(value);
            }}
          />
          <FilterDropdown
            label="Status"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={(value) => {
              setCurrentPage(1);
              setStatusFilter(value);
            }}
          />
        </div>

        <div className="mb-4">
          <InsuranceProtectStats
            total={stats.totalInsurances}
            paid={stats.paid}
            notPaid={stats.notPaid}
            totalBilled={stats.totalBilled}
          />
        </div>

        <p className="ip-table-caption mb-3">
          All insurances and Skor Protect policies for this landlord across workspaces &middot; use filters to narrow billing
        </p>

        {loading ? (
          <ModalSkeleton />
        ) : (
          <InsuranceProtectTable
            data={list}
            total={total}
            currentPage={currentPage}
            limit={LIMIT}
            onPageChange={setCurrentPage}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default InsuranceProtectDetailsModal;
